/**
 * EditPrivateLabelModal Component
 * Modal for editing existing private labels
 */
import React, { useState } from 'react';
import { X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const EditPrivateLabelModal = ({ label, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: label.name || '',
    description: label.description || '',
    email: label.email || '',
    website: label.website || '',
    genres: label.genres || [],
    accepting_demos: label.accepting_demos || false,
    demo_submission_email: label.demo_submission_email || ''
  });
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      alert('Label name is required');
      return;
    }
    
    if (formData.accepting_demos && !formData.demo_submission_email.trim()) {
      alert('Demo submission email is required when accepting demos');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('beatseek_token');
      const response = await fetch(`${API}/api/private-labels/${label.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        onSuccess && onSuccess();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to update label');
      }
    } catch (error) {
      console.error('Error updating label:', error);
      alert('Failed to update label');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this label? This cannot be undone.')) {
      return;
    }
    
    setDeleting(true);
    try {
      const token = localStorage.getItem('beatseek_token');
      const response = await fetch(`${API}/api/private-labels/${label.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        onSuccess && onSuccess();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to delete label');
      }
    } catch (error) {
      console.error('Error deleting label:', error);
      alert('Failed to delete label');
    } finally {
      setDeleting(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '0.6rem',
    borderRadius: '8px',
    border: '1px solid rgba(251, 191, 36, 0.3)',
    background: 'rgba(30, 20, 50, 0.8)',
    color: '#fff',
    fontSize: '0.85rem',
    outline: 'none',
    boxSizing: 'border-box'
  };

  const labelStyle = {
    display: 'block',
    color: '#fbbf24',
    fontSize: '0.85rem',
    marginBottom: '0.4rem'
  };

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(0, 0, 0, 0.9)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '1rem'
    }}>
      <div 
        data-testid="edit-label-modal"
        style={{
          background: 'linear-gradient(135deg, #1a1025 0%, #2d1f3d 50%, #1a1025 100%)',
          borderRadius: '16px',
          border: '2px solid rgba(251, 191, 36, 0.4)',
          width: '100%',
          maxWidth: '500px',
          maxHeight: '80vh',
          overflow: 'auto',
          padding: '1.5rem'
        }}
      >
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '1.5rem'
        }}>
          <h3 style={{ color: '#fbbf24', fontSize: '1.2rem', margin: 0 }}>
            ✏️ Edit Label
          </h3>
          <button 
            onClick={onClose}
            data-testid="close-edit-label-modal"
            style={{
              background: 'none',
              border: 'none',
              color: 'rgba(255,255,255,0.6)',
              cursor: 'pointer',
              padding: '0.25rem'
            }}
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Name */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={labelStyle}>Label Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              data-testid="edit-label-name-input"
              style={inputStyle}
            />
          </div>

          {/* Description */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={labelStyle}>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              data-testid="edit-label-description-input"
              style={{ ...inputStyle, resize: 'vertical' }}
            />
          </div>

          {/* Email */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={labelStyle}>Contact Email</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              data-testid="edit-label-email-input"
              style={inputStyle}
            />
          </div>

          {/* Website */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={labelStyle}>Website</label>
            <input
              type="url"
              value={formData.website}
              onChange={(e) => setFormData({ ...formData, website: e.target.value })}
              data-testid="edit-label-website-input"
              style={inputStyle}
            />
          </div>

          {/* Accepting Demos Toggle */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              cursor: 'pointer'
            }}>
              <input
                type="checkbox"
                checked={formData.accepting_demos}
                onChange={(e) => setFormData({ ...formData, accepting_demos: e.target.checked })}
                data-testid="edit-accepting-demos-checkbox"
                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
              />
              <span style={{ color: '#22c55e', fontSize: '0.85rem', fontWeight: '500' }}>
                Accepting Demos
              </span>
            </label>
          </div>

          {/* Demo Email (shown when accepting demos) */}
          {formData.accepting_demos && (
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ ...labelStyle, color: '#22c55e' }}>
                Demo Submission Email *
              </label>
              <input
                type="email"
                value={formData.demo_submission_email}
                onChange={(e) => setFormData({ ...formData, demo_submission_email: e.target.value })}
                required={formData.accepting_demos}
                data-testid="edit-demo-email-input"
                style={{
                  ...inputStyle,
                  border: '1px solid rgba(34, 197, 94, 0.4)'
                }}
              />
            </div>
          )}

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.5rem' }}>
            <button
              type="button"
              onClick={handleDelete}
              disabled={deleting}
              data-testid="delete-label-btn"
              style={{
                padding: '0.65rem 1rem',
                borderRadius: '8px',
                border: '1px solid rgba(239, 68, 68, 0.5)',
                background: 'rgba(239, 68, 68, 0.1)',
                color: '#ef4444',
                fontSize: '0.85rem',
                fontWeight: '500',
                cursor: deleting ? 'not-allowed' : 'pointer'
              }}
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </button>
            <button
              type="submit"
              disabled={loading}
              data-testid="save-label-btn"
              style={{
                flex: 1,
                padding: '0.65rem',
                borderRadius: '8px',
                border: 'none',
                background: loading ? 'rgba(251, 191, 36, 0.3)' : 'linear-gradient(135deg, #fbbf24, #f59e0b)',
                color: loading ? 'rgba(0,0,0,0.5)' : '#000',
                fontSize: '0.85rem',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer'
              }}
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditPrivateLabelModal;
